'use client'

import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState, useTransition } from 'react'
import { cn, Input, Label, TextArea, TextField, toast } from '@heroui/react'

import { TurnstileWidget } from '@/components/forms/turnstile-widget'
import { ContactMethodPicker } from '@/components/leads/contact-method-picker'
import { LeadSuccess } from '@/components/leads/lead-success'
import { VehiclePicker, type VehicleValue } from '@/components/leads/vehicle-picker'
import { Button } from '@/components/ui/button'
import { CompatSelect as Select, ListBoxItem } from '@/components/ui/compat-select'
import { Icon } from '@/components/ui/icon'
import { CONTACT_PATHS } from '@/config/contact'
import { pushFormSubmit } from '@/lib/analytics/funnel-events'
import { useFeatureFlags } from '@/lib/features/feature-flags-context'
import type { IconName } from '@/config/icons'
import type { SilhouetteKind } from '@/lib/quote/vehicle-silhouette'
import { getVehicleSilhouette, submitQuoteRequest } from '@/lib/services/actions/inquiry'
import { QUOTE_PHOTO_ANGLES, type InquiryContactMethod } from '@/lib/validation/input/inquiry-schema'

import { VehiclePhotoCards, type QuotePhotos } from './vehicle-photo-cards'

interface QuoteRequestFormProps {
  locationId: string
  defaultName?: string
  defaultEmail?: string
}

/**
 * What the customer needs drives which fields appear. `vehicle` shows the
 * year/make/model picker, `company` a business name, `colour` a colour/finish
 * field, `size` a dimensions/quantity field, `instant` a nudge to the price-
 * book estimator, and `fleet` a pointer to the dedicated fleet form.
 */
interface Need {
  key: string
  label: string
  icon: IconName
  vehicle?: boolean
  company?: boolean
  colour?: boolean
  size?: boolean
  instant?: boolean
  fleet?: boolean
  placeholder: string
}

const NEEDS: Need[] = [
  {
    key: 'colour-wrap',
    label: 'Colour change wrap',
    icon: 'cw-colour-change',
    vehicle: true,
    colour: true,
    instant: true,
    placeholder: 'Full or partial wrap? Any chrome delete, roof, or accents? Anything on the paint we should know about?',
  },
  {
    key: 'commercial-graphics',
    label: 'Commercial graphics or decals',
    icon: 'cw-decal',
    vehicle: true,
    company: true,
    placeholder: 'What goes on the vehicle: logo, phone, website, full livery? Do you have artwork already?',
  },
  {
    key: 'ppf',
    label: 'Paint protection film',
    icon: 'cw-paint-protection',
    vehicle: true,
    instant: true,
    placeholder: 'Front end, full front, full body, or specific panels? Gloss or matte finish?',
  },
  {
    key: 'signs-printing',
    label: 'Signs, murals, or printing',
    icon: 'cw-brush',
    company: true,
    size: true,
    placeholder: 'Where does it go (storefront, wall, window, yard)? Indoor or outdoor?',
  },
  {
    key: 'fleet',
    label: 'Fleet (2+ vehicles)',
    icon: 'cw-fleet',
    company: true,
    fleet: true,
    placeholder: 'How many vehicles, what types, and when do you need them on the road?',
  },
  {
    key: 'other',
    label: 'Something else',
    icon: 'cw-info',
    vehicle: true,
    placeholder: 'Tell us what you have in mind and we will point you at the right service.',
  },
]

const TIMELINES = ['As soon as possible', 'Within 1 month', '1-3 months', '3+ months', 'Just exploring']

/** Map a ?service= slug or name from a service page onto a need key. */
function needFromService(service: string | null): string | null {
  if (!service) return null
  const s = service.toLowerCase()
  if (s.includes('fleet')) return 'fleet'
  if (s.includes('protection') || s.includes('ppf')) return 'ppf'
  if (s.includes('graphic') || s.includes('decal') || s.includes('skin') || s.includes('livery')) return 'commercial-graphics'
  if (s.includes('sign') || s.includes('print') || s.includes('mural') || s.includes('banner')) return 'signs-printing'
  if (s.includes('wrap') || s.includes('colour') || s.includes('color')) return 'colour-wrap'
  return null
}

export function QuoteRequestForm({ locationId, defaultName = '', defaultEmail = '' }: QuoteRequestFormProps) {
  const params = useSearchParams()
  const prefill = useMemo(
    () => ({
      service: params.get('service'),
      need: needFromService(params.get('service')) ?? params.get('need'),
      year: params.get('year') ?? '',
      make: params.get('make') ?? '',
      model: params.get('model') ?? '',
      colour: params.get('colour') ?? '',
    }),
    [params]
  )

  const [isPending, startTransition] = useTransition()
  const [submitted, setSubmitted] = useState(false)
  const inFlightRef = useRef(false)

  const [needKey, setNeedKey] = useState<string>(prefill.need && NEEDS.some((n) => n.key === prefill.need) ? prefill.need : '')
  const [vehicle, setVehicle] = useState<VehicleValue>({ year: '', make: '', model: '' })
  const [photos, setPhotos] = useState<QuotePhotos>({})
  const [silhouette, setSilhouette] = useState<SilhouetteKind>('sedan')
  const [colour, setColour] = useState(prefill.colour)
  const [size, setSize] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [timeline, setTimeline] = useState('')
  const [details, setDetails] = useState('')
  const [name, setName] = useState(defaultName)
  const [email, setEmail] = useState(defaultEmail)
  const [phone, setPhone] = useState('')
  const [contactMethod, setContactMethod] = useState<InquiryContactMethod>('email')
  const [website, setWebsite] = useState('')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  // Bumped after a rejected submit: the token is single-use, so the widget has
  // to draw a fresh one before the customer can try again.
  const [turnstileNonce, setTurnstileNonce] = useState(0)

  const need = NEEDS.find((n) => n.key === needKey)
  const needsPhone = contactMethod !== 'email'
  const hasVehicle = Boolean(vehicle.year && vehicle.make && vehicle.model)

  // The photo cards redraw for the picked body type. Only a fully picked
  // vehicle is looked up; a partial pick or a free-text one stays a sedan.
  useEffect(() => {
    if (!hasVehicle) {
      setSilhouette('sedan')
      return
    }
    let cancelled = false
    getVehicleSilhouette(Number(vehicle.year), vehicle.make, vehicle.model)
      .then((kind) => {
        if (!cancelled) setSilhouette(kind)
      })
      .catch(() => {
        if (!cancelled) setSilhouette('sedan')
      })
    return () => {
      cancelled = true
    }
  }, [hasVehicle, vehicle.year, vehicle.make, vehicle.model])

  const { instantQuoteEnabled } = useFeatureFlags()
  const instantHref = useMemo(() => {
    const q = new URLSearchParams()
    if (vehicle.make) q.set('make', vehicle.make)
    if (vehicle.model) q.set('model', vehicle.model)
    if (vehicle.year) q.set('year', vehicle.year)
    const qs = q.toString()
    return qs ? `${CONTACT_PATHS.instantQuote}?${qs}` : CONTACT_PATHS.instantQuote
  }, [vehicle])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (inFlightRef.current) return
    if (!need) {
      toast.danger('Pick what you need', { description: 'Choose one of the options at the top of the form.' })
      return
    }
    if (needsPhone && !phone.trim()) {
      toast.danger('Phone number needed', { description: 'Add a number so we can text or call you.' })
      return
    }
    inFlightRef.current = true

    const messageParts = [
      details.trim(),
      need.colour && colour.trim() ? `Colour / finish: ${colour.trim()}` : '',
      need.size && size.trim() ? `Size / quantity: ${size.trim()}` : '',
      prefill.service ? `Came from service: ${prefill.service}` : '',
    ].filter(Boolean)

    startTransition(async () => {
      try {
        // FormData so the vehicle photos ride along with the text fields.
        const fd = new FormData()
        const set = (k: string, v: string | undefined) => {
          if (v) fd.set(k, v)
        }
        set('source', 'quote-form')
        set('name', name)
        set('email', email)
        set('phone', phone)
        set('contactMethod', contactMethod)
        set('companyName', need.company ? companyName : undefined)
        set('vehicleYear', need.vehicle ? vehicle.year : undefined)
        set('vehicleMake', need.vehicle ? vehicle.make : undefined)
        set('vehicleModel', need.vehicle ? vehicle.model : undefined)
        set('serviceInterest', need.label)
        set('timeline', timeline)
        set('message', messageParts.join('\n'))
        set('shopLocationId', locationId)
        set('website', website)
        set('turnstileToken', turnstileToken ?? undefined)
        if (need.vehicle) {
          for (const angle of QUOTE_PHOTO_ANGLES) {
            const f = photos[angle.key]
            if (f) fd.append(`photo:${angle.key}`, f, f.name)
          }
        }
        const result = await submitQuoteRequest(fd)
        if (result.success) {
          if (result.data?.id && result.data.id !== 'ignored')
            pushFormSubmit('quote', result.data.id, { email, phone })
          toast.success('Quote request sent', { description: 'We reply with a number within one business day.' })
          setSubmitted(true)
          setPhotos({})
        } else {
          setTurnstileNonce((n) => n + 1)
          toast.danger('Something went wrong', { description: result.error || 'Please try again.' })
        }
      } finally {
        inFlightRef.current = false
      }
    })
  }

  if (submitted) {
    return (
      <LeadSuccess
        title="Quote request received"
        onReset={() => {
          setSubmitted(false)
          setDetails('')
          setTimeline('')
        }}
        resetLabel="Send another request"
        actions={
          <Link href={CONTACT_PATHS.book}>
            <Button variant="primary">Book an appointment</Button>
          </Link>
        }
      >
        <p>A person quotes every request. Standard work gets a number the same business day; multi-vehicle or design-from-scratch projects take one to three days to scope properly.</p>
        <p>Forgot a photo? Reply to our email with it, or text it to the shop.</p>
      </LeadSuccess>
    )
  }

  return (
    <div className="rounded-2xl border border-border bg-surface p-6 sm:p-8">
      <form onSubmit={handleSubmit} className="space-y-6" aria-label="Quote request form">
        {/* Step 1: what do you need */}
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-foreground">What do you need?</legend>
          <div role="radiogroup" aria-label="Type of work" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {NEEDS.map((n) => {
              const active = n.key === needKey
              return (
                <button
                  key={n.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setNeedKey(n.key)}
                  data-testid={`need-${n.key}`}
                  className={cn(
                    'flex min-h-12 items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors',
                    active
                      ? 'border-accent bg-accent text-accent-foreground'
                      : 'border-border bg-background text-foreground hover:border-foreground/30'
                  )}
                >
                  <Icon icon={n.icon} className="text-xl" aria-hidden="true" />
                  {n.label}
                </button>
              )
            })}
          </div>
        </fieldset>

        {need?.fleet && (
          <div className="rounded-xl border border-accent bg-background p-4 text-sm">
            <p className="font-semibold text-foreground">Fleets get their own form.</p>
            <p className="mt-1 text-muted">
              It asks the questions the fleet team needs (vehicle mix, count, rollout timing) and skips the ones they do
              not. You can still send this form if you prefer.
            </p>
            <Link href={CONTACT_PATHS.fleetQuote} className="mt-3 inline-flex">
              <Button variant="primary" size="sm">
                Go to the fleet quote form
              </Button>
            </Link>
          </div>
        )}

        {need && (
          <>
            {/* Step 2: the work */}
            <div className="space-y-4">
              {need.vehicle && (
                <>
                  <div className="space-y-1.5">
                    <span className="block text-sm font-medium text-foreground">Vehicle</span>
                    <VehiclePicker
                      value={vehicle}
                      onChange={setVehicle}
                      initial={{ year: prefill.year, make: prefill.make, model: prefill.model }}
                    />
                  </div>
                  <VehiclePhotoCards kind={silhouette} photos={photos} onChange={setPhotos} />
                </>
              )}

              {need.company && (
                <TextField value={companyName} onChange={setCompanyName}>
                  <Label>Company{need.fleet ? '' : ' (if this is for a business)'}</Label>
                  <Input variant="outline" autoComplete="organization" />
                </TextField>
              )}

              {need.colour && (
                <TextField value={colour} onChange={setColour}>
                  <Label>Colour or finish you have in mind</Label>
                  <Input variant="outline" placeholder="e.g. Satin Dark Grey, Gloss Blue Fire, not sure yet" />
                </TextField>
              )}

              {need.size && (
                <TextField value={size} onChange={setSize}>
                  <Label>Size or quantity</Label>
                  <Input variant="outline" placeholder="e.g. 4 ft x 8 ft, two window panels, 50 decals" />
                </TextField>
              )}

              <div className="space-y-1.5">
                <span className="block text-sm font-medium text-foreground">Timeline</span>
                <Select
                  aria-label="Timeline"
                  placeholder="When do you need it?"
                  selectedKeys={timeline ? [timeline] : []}
                  onSelectionChange={(keys) => {
                    const val = Array.from(keys as Iterable<unknown>)[0]
                    setTimeline(typeof val === 'string' ? val : '')
                  }}
                  variant="outline"
                >
                  {TIMELINES.map((t) => (
                    <ListBoxItem key={t} id={t}>
                      {t}
                    </ListBoxItem>
                  ))}
                </Select>
              </div>

              <TextField value={details} onChange={setDetails}>
                <Label>Details</Label>
                <TextArea variant="outline" rows={4} placeholder={need.placeholder} />
              </TextField>

              {instantQuoteEnabled && need.instant && hasVehicle && (
                <p className="rounded-xl border border-border bg-background p-3 text-sm text-muted">
                  Want a number right now?{' '}
                  <Link href={instantHref} className="font-semibold text-foreground underline">
                    Get an instant estimate for your {vehicle.year} {vehicle.make} {vehicle.model}
                  </Link>{' '}
                  while you wait for the firm quote.
                </p>
              )}
            </div>

            {/* Step 3: you */}
            <div className="space-y-4 border-t border-border pt-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <TextField value={name} onChange={setName} isRequired>
                  <Label>Your name</Label>
                  <Input variant="outline" autoComplete="name" />
                </TextField>
                <TextField value={email} onChange={setEmail} isRequired>
                  <Label>Email</Label>
                  <Input type="email" variant="outline" autoComplete="email" />
                </TextField>
              </div>
              <div className="space-y-2">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <TextField value={phone} onChange={setPhone} isRequired={needsPhone} className="flex-1">
                    <Label>Phone{needsPhone ? '' : ' (optional)'}</Label>
                    <Input type="tel" inputMode="tel" variant="outline" autoComplete="tel" />
                  </TextField>
                  <ContactMethodPicker value={contactMethod} onChange={setContactMethod} />
                </div>
                <p className="text-xs text-muted">How would you like the quote?</p>
              </div>
            </div>

            <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
              <label>
                Website
                <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
              </label>
            </div>

            <TurnstileWidget onToken={setTurnstileToken} resetSignal={turnstileNonce} />

            <Button type="submit" variant="primary" size="lg" className="w-full" isLoading={isPending} isDisabled={isPending}>
              {isPending ? 'Sending...' : 'Request my quote'}
            </Button>
          </>
        )}
      </form>
    </div>
  )
}
